import type { ChangeEvent, FocusEvent } from 'react';
import { Field, type FieldProps } from './Field';

type Base = Omit<FieldProps, 'children' | 'group'> & {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  autoComplete?: string;
  inputMode?: 'text' | 'email' | 'tel' | 'numeric' | 'decimal';
  maxLength?: number;
};

export function TextField({ value, onChange, onBlur, placeholder, autoComplete, inputMode, maxLength, type = 'text', min, max, step, ...field }: Base & { type?: string; min?: number; max?: number; step?: number }) {
  return (
    <Field {...field}>
      {({ id, describedBy, invalid }) => (
        <input
          id={id}
          className="cbf-input"
          type={type}
          value={value}
          placeholder={placeholder}
          autoComplete={autoComplete}
          inputMode={inputMode}
          maxLength={maxLength}
          min={min}
          max={max}
          step={step}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          aria-required={field.required || undefined}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
          onBlur={onBlur}
        />
      )}
    </Field>
  );
}

export function TextareaField({ value, onChange, onBlur, placeholder, maxLength, rows = 4, ...field }: Base & { rows?: number }) {
  return (
    <Field {...field}>
      {({ id, describedBy, invalid }) => (
        <textarea
          id={id}
          className="cbf-textarea"
          value={value}
          placeholder={placeholder}
          maxLength={maxLength}
          rows={rows}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          aria-required={field.required || undefined}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
          onBlur={onBlur}
        />
      )}
    </Field>
  );
}

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export function SelectField({ value, onChange, onBlur, options, placeholder, ...field }: Omit<Base, 'placeholder' | 'autoComplete' | 'inputMode' | 'maxLength'> & { options: SelectOption[]; placeholder?: string }) {
  return (
    <Field {...field}>
      {({ id, describedBy, invalid }) => (
        <select
          id={id}
          className="cbf-select"
          value={value}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          aria-required={field.required || undefined}
          onChange={(e: ChangeEvent<HTMLSelectElement>) => onChange(e.target.value)}
          onBlur={(_e: FocusEvent<HTMLSelectElement>) => onBlur?.()}
        >
          <option value="">{placeholder ?? 'Select…'}</option>
          {options.map((o) => (
            <option key={o.value} value={o.value} disabled={o.disabled}>
              {o.label}
            </option>
          ))}
        </select>
      )}
    </Field>
  );
}
